/**
 * Shared fake-progress logic for the AI scanner pages (meal photo,
 * label, equipment, and the upcoming workout scanner).
 *
 * While a scan request is in flight we have no real progress signal,
 * so all scanners show the same fake progress: an asymptotic curve
 * that rises fast at first and then slows down, never quite reaching
 * the cap until the scan actually finishes. The status line cycles
 * through a fixed list of messages on a fixed step.
 *
 * The constants are the converged values from the scanner refactor
 * (see design-docs/scanner-shared-components.md, decisions #1-#3):
 * cap 98%, tau 7s, 200ms ticks, status step 2.5s, and the bar fills
 * to 100% when the scan stops.
 */

/** Time constant of the curve: ~63% at tau, ~86% at 2*tau, ~95% at 3*tau. */
export const SCAN_PROGRESS_TAU_MS = 7000;
/** The fake progress never passes this while the scan is in flight.
 *  NOTE: cs-scanner-progress-bar.html duplicates this value as a literal
 *  (`@if (progress < 98)` hides the pulse at the cap) because templates
 *  cannot import TS constants — change both together. */
export const SCAN_PROGRESS_CAP_PCT = 98;
/** How often the progress (and status text) update. */
export const SCAN_PROGRESS_TICK_MS = 200;
/** How long each status message stays before the next one shows. */
export const SCAN_STATUS_STEP_MS = 2500;

/**
 * The asymptotic fake-progress curve: 100 * (1 - e^(-t/tau)), capped.
 * Pure function of elapsed time, so it is easy to test and cannot
 * drift between scanners.
 */
export function progressCurveAsymptotic(
  elapsedMs: number,
  tauMs: number = SCAN_PROGRESS_TAU_MS,
  capPct: number = SCAN_PROGRESS_CAP_PCT,
): number {
  return Math.min(capPct, 100 * (1 - Math.exp(-elapsedMs / tauMs)));
}

/**
 * Which status message to show after `elapsedMs`: one step per
 * `stepMs`, holding the last message once the list is exhausted.
 */
export function scanStatusMessageIndex(
  elapsedMs: number,
  messageCount: number,
  stepMs: number = SCAN_STATUS_STEP_MS,
): number {
  if (messageCount <= 0) return -1;
  return Math.min(messageCount - 1, Math.floor(elapsedMs / stepMs));
}

/**
 * The shared fake-progress driver: owns the interval timer and exposes
 * `progress` (0-100) and `statusText` for the page to bind.
 *
 * The page provides the status messages as functions (so translations
 * resolve lazily) and an `onTick` callback where it syncs its own
 * fields and calls `markForCheck()`. The page is responsible for
 * calling `stop()` when the view goes away (ionViewWillLeave /
 * ngOnDestroy) — same contract the per-page timers had before.
 */
export class FakeScanProgress {
  /** Current fill percentage, 0-100. */
  progress = 0;
  /** Current status line, already translated. */
  statusText = '';

  private timer: ReturnType<typeof setInterval> = null;
  private startTime = 0;

  constructor(
    private readonly statusMessages: (() => string)[],
    private readonly onTick: () => void,
  ) {}

  /** (Re)start from zero. Also used for the retry-with-another-model reset. */
  start() {
    this.clearTimer();
    this.startTime = Date.now();
    this.progress = 0;
    this.statusText = this.messageAt(0);
    this.timer = setInterval(() => {
      const elapsed = Date.now() - this.startTime;
      this.progress = progressCurveAsymptotic(elapsed);
      this.statusText = this.messageAt(elapsed);
      this.onTick();
    }, SCAN_PROGRESS_TICK_MS);
  }

  /** Stop ticking and fill the bar (decision #2: stop means "done", the
   *  bar completes before the page switches views or shows an alert). */
  stop() {
    this.clearTimer();
    this.progress = 100;
  }

  private messageAt(elapsedMs: number): string {
    const index = scanStatusMessageIndex(elapsedMs, this.statusMessages.length);
    return index < 0 ? '' : this.statusMessages[index]();
  }

  private clearTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}
